<template>
  <v-container>
    <div class="d-flex flex-column justify-center align-center loading-tips-card">
      <v-progress-circular indeterminate color="accent" size="48" width="4" />
      <div v-if="message" class="mt-3 text-body-2 text-medium-emphasis">{{ message }}</div>
      <transition name="tip-fade" mode="out-in">
        <div
            v-if="currentTip"
            :key="currentIndex"
            class="loading-tip mt-4 text-body-2 text-medium-emphasis"
        >
          {{ currentTip.text }}
        </div>
      </transition>
    </div>
  </v-container>
</template>

<script>
  import {
    getLoadingTips,
    getCurrentSharedTip,
    subscribeToSharedRotation
  } from '@/services/LoadingTips'

  export default {
    name: 'LoadingTipsCard',
    props: {
      // Optional: filter to a specific platform
      // ('android','kindle','ios','mac','windows','linux','chromebook').
      // Setting this disables shared rotation — the card rotates independently
      // over the filtered subset.
      platform: { type: String, default: null },
      // How often to rotate tips, in ms. Pass 0 to disable rotation.
      rotateMs: { type: Number, default: 7000 },
      // Optional short message shown above the tip.
      message: { type: String, default: null },
      // When true (default), smoothly scrolls the card into view on mount.
      // Useful when the card replaces a "load more" button below the fold;
      // a no-op when the card is already fully visible.
      autoScroll: { type: Boolean, default: true }
    },
    data() {
      return {
        // Used only when platform filter is active (local rotation mode).
        localTips: [],
        // Always the index of the currently-displayed tip in whichever list
        // is active (shared or local). Used as the transition :key.
        currentIndex: 0,
        // The currently displayed tip object (shared mode reads this from
        // the rotator's callbacks; local mode reads from localTips).
        sharedTip: null,
        localRotateTimer: null,
        unsubscribeShared: null
      }
    },
    computed: {
      useShared() {
        return !this.platform
      },
      currentTip() {
        if (this.useShared) return this.sharedTip
        return this.localTips[this.currentIndex] || null
      }
    },
    created() {
      if (this.useShared) {
        const { index, tip } = getCurrentSharedTip()
        this.currentIndex = index
        this.sharedTip = tip
        this.unsubscribeShared = subscribeToSharedRotation(
            (index, tip) => {
              this.currentIndex = index
              this.sharedTip = tip
            },
            this.rotateMs
        )
      } else {
        const all = getLoadingTips()
        this.localTips = all.filter(
            t => t.platforms.includes('all') || t.platforms.includes(this.platform)
        )
        this.shuffle(this.localTips)
        if (this.rotateMs > 0 && this.localTips.length > 1) {
          this.localRotateTimer = setInterval(this.nextLocalTip, this.rotateMs)
        }
      }
    },
    mounted() {
      if (this.autoScroll) {
        this.$nextTick(() => {
          try {
            this.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          } catch (e) { /* older browsers without options support */ }
        })
      }
    },
    beforeUnmount() {
      if (this.unsubscribeShared) this.unsubscribeShared()
      if (this.localRotateTimer) clearInterval(this.localRotateTimer)
    },
    methods: {
      nextLocalTip() {
        this.currentIndex = (this.currentIndex + 1) % this.localTips.length
      },
      shuffle(arr) {
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1))
          ;[arr[i], arr[j]] = [arr[j], arr[i]]
        }
      }
    }
  }
</script>

<style scoped>
  .loading-tips-card {
    max-width: 520px;
    margin: 0 auto;
    padding: 24px 16px;
    text-align: center;
  }
  .loading-tip {
    min-height: 4em;
    line-height: 1.5;
  }
  .tip-fade-enter-active,
  .tip-fade-leave-active {
    transition: opacity 0.4s ease;
  }
  .tip-fade-enter-from,
  .tip-fade-leave-to {
    opacity: 0;
  }
</style>
